import { useState, useCallback, useMemo, useRef } from 'react';

import type { NormalizedPolicy } from '@/features/policies-comparison';

const STORAGE_PREFIX = 'policies-comparison-column-order';

function storageKey(clientId: string) {
  return `${STORAGE_PREFIX}:${clientId}`;
}

function loadColumnOrder(clientId: string): string[] | null {
  try {
    const stored = localStorage.getItem(storageKey(clientId));
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function saveColumnOrder(clientId: string, order: string[]) {
  try {
    localStorage.setItem(storageKey(clientId), JSON.stringify(order));
  } catch {
    // storage full or unavailable
  }
}

function reconcileOrder(base: string[] | null, validIds: Set<string>): string[] | null {
  if (!base) return null;
  const filtered = base.filter(id => validIds.has(id));
  return filtered.length > 0 ? filtered : null;
}

export function useColumnReorder(data: NormalizedPolicy[], clientId: string) {
  const [columnOrder, setColumnOrder] = useState<string[] | null>(() => loadColumnOrder(clientId));
  const [previewOrder, setPreviewOrder] = useState<string[] | null>(null);

  const clientIdRef = useRef(clientId);
  if (clientIdRef.current !== clientId) {
    clientIdRef.current = clientId;
    setColumnOrder(loadColumnOrder(clientId));
    setPreviewOrder(null);
  }

  const dataRef = useRef(data);
  dataRef.current = data;

  const effectiveOrder = useMemo(() => {
    const validIds = new Set(data.map(p => p.fundingSourceId));
    const base = reconcileOrder(previewOrder, validIds) ?? reconcileOrder(columnOrder, validIds);
    if (!base) return data.map(p => p.fundingSourceId);

    const knownIds = new Set(base);
    const newIds = data.map(p => p.fundingSourceId).filter(id => !knownIds.has(id));
    return [...base, ...newIds];
  }, [previewOrder, columnOrder, data]);

  const effectiveOrderRef = useRef(effectiveOrder);
  effectiveOrderRef.current = effectiveOrder;

  const orderedData = useMemo(() => {
    const dataMap = new Map(data.map(policy => [policy.fundingSourceId, policy]));
    return effectiveOrder
      .map(id => dataMap.get(id))
      .filter((policy): policy is NormalizedPolicy => policy !== undefined);
  }, [data, effectiveOrder]);

  const hoverColumn = useCallback((dragId: string, hoverId: string) => {
    const current = effectiveOrderRef.current;
    const dragIndex = current.indexOf(dragId);
    const hoverIndex = current.indexOf(hoverId);
    if (dragIndex === -1 || hoverIndex === -1 || dragIndex === hoverIndex) return;

    const next = [...current];
    next.splice(dragIndex, 1);
    next.splice(hoverIndex, 0, current[dragIndex]);
    setPreviewOrder(next);
  }, []);

  const commitDrop = useCallback(() => {
    const order = effectiveOrderRef.current;
    setColumnOrder(order);
    saveColumnOrder(clientIdRef.current, order);
    setPreviewOrder(null);
  }, []);

  const cancelDrag = useCallback(() => {
    setPreviewOrder(null);
  }, []);

  return { orderedData, hoverColumn, commitDrop, cancelDrag };
}
